import { Alert, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@mui/material';
import LoadingButton from '@/components/ui/LoadingButton/LoadingButton.tsx';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useEffect } from 'react';
import BackButton from '@/components/ui/BackButton/BackButton.tsx';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs from 'dayjs';
import { MuiTelInput } from 'mui-tel-input';
import parsePhoneNumberFromString from 'libphonenumber-js';
import { EditReservationRequest } from '@/types/request/EditReservationRequest';
import useReservations from '@/api/useReservations.ts';
import LoaderWrapper from '@/components/ui/LoaderWrapper/LoaderWrapper.tsx';
import useReservationEdit from '@/api/useReservationEdit.ts';
import EditIcon from '@mui/icons-material/Edit';

const schema = z.object({
  client: z
    .string()
    .min(1, { message: 'This field is required.' })
    .max(500, { message: 'Must be less than 150 characters.' }),
  phone: z
    .string()
    .min(1, { message: 'This field is required.' })
    .transform((arg, ctx) => {
      const phone = parsePhoneNumberFromString(arg, {
        // set this to use a default country when the phone number omits country code
        defaultCountry: 'US',

        // set to false to require that the whole string is exactly a phone number,
        // otherwise, it will search for a phone number anywhere within the string
        extract: false,
      });

      // when it's good
      if (phone && phone.isValid()) {
        return phone.number;
      }

      // when it's not
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Invalid phone number',
      });
      return z.NEVER;
    }),
  date: z
    .string()
    .min(1, { message: 'This field is required.' })
    .max(500, { message: 'Must be less than 100 characters.' }),
  people: z
    .string()
    .min(1, { message: 'This field is required.' })
    .max(500, { message: 'Must be less than 50 characters.' }),
});

type Schema = z.infer<typeof schema>;

type EditReservationModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm?: () => void;
  reservationId: number;
};
function EditReservationModal({ isOpen, onClose, onConfirm, reservationId }: EditReservationModalProps) {
  const { t } = useTranslation();
  const {
    mutateAsync: editReservation,
    isError: isErrorEditReservation,
    isPending: isLoadingEditReservation,
    isSuccess: isSuccessEditReservation,
    error: errorEditReservation,
  } = useReservationEdit();

  const {
    data: currentData,
    isLoading: isLoadingCurrentData,
    isSuccess,
  } = useReservations(true, {
    id: reservationId,
  });

  const { handleSubmit, control, setValue } = useForm<Schema>({
    resolver: zodResolver(schema),
    defaultValues: {
      client: '',
      phone: '',
      date: '',
      people: '',
    },
  });

  const handleUpdateReservation = (data: Schema) => {
    void (async () => {
      try {
        if (isLoadingEditReservation || !currentData || !currentData[0]) {
          return;
        }

        const request: EditReservationRequest = {
          id: reservationId,
          restaurantId: currentData[0].restaurant_id.id,
          client: data.client,
          phone: data.phone,
          date: data.date,
          people: parseInt(data.people),
        };

        await editReservation(request);

        if (onConfirm) {
          onConfirm();
        }

        onClose();
      } catch (error) {
        console.error('Error editing reservation: ', error);
      }
    })();
  };

  useEffect(() => {
    if (isSuccess && currentData[0]) {
      const { client, phone, date, people } = currentData[0];
      setValue('client', client);
      // @ts-expect-error - phone is string
      setValue('phone', phone);
      setValue('date', date);
      setValue('people', `${people}`);
    }
  }, [currentData, isLoadingCurrentData, isSuccess, setValue]);

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="sm" fullWidth>
      <form className="modal-form" onSubmit={handleSubmit(handleUpdateReservation)}>
        <DialogTitle>{t('modals.editReservationModal.title')}</DialogTitle>
        <DialogContent>
          <LoaderWrapper isLoading={isLoadingCurrentData}>
            <div className="pt-2 flex flex-col gap-4">
              <div className="flex flex-col gap-2 empty:hidden">
                {isSuccessEditReservation && (
                  <Alert severity="success">{t('modals.editReservationModal.successMessage')}</Alert>
                )}
                {isErrorEditReservation && <Alert severity="error">{errorEditReservation?.message as string}</Alert>}
              </div>
              <div className="grid grid-cols-2 gap-4 w-full">
                <Controller
                  control={control}
                  name="date"
                  render={({ field: { onChange, value }, formState: { errors } }) => (
                    <DateTimePicker
                      className="col-span-2 lg:col-span-1"
                      onChange={(value) => {
                        if (!value) {
                          onChange('');
                        }
                        onChange(value?.toISOString());
                      }}
                      value={value === '' ? null : dayjs.utc(value)}
                      slotProps={{
                        field: {
                          clearable: true,
                        },
                        textField: {
                          size: 'medium',
                          error: !!errors.date,
                          helperText: errors.date?.message,
                          label: t('label.date'),
                          placeholder: t('placeholder.date'),
                          InputLabelProps: {
                            shrink: true,
                          },
                        },
                      }}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="people"
                  render={({ field: { onChange, value }, formState: { errors } }) => (
                    <TextField
                      className="col-span-2 lg:col-span-1"
                      error={!!errors.people}
                      helperText={errors.people?.message}
                      value={value}
                      onChange={onChange}
                      type="number"
                      slotProps={{
                        input: {
                          inputProps: {
                            min: 1,
                          },
                        },
                        inputLabel: {
                          shrink: true,
                        },
                      }}
                      variant="outlined"
                      label={t('label.people')}
                      placeholder={t('placeholder.people')}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="client"
                  render={({ field: { onChange, value }, formState: { errors } }) => (
                    <TextField
                      className="col-span-2"
                      error={!!errors.client}
                      helperText={errors.client?.message}
                      value={value}
                      onChange={onChange}
                      slotProps={{
                        inputLabel: {
                          shrink: true,
                        },
                      }}
                      variant="outlined"
                      label={t('label.client')}
                      placeholder={t('placeholder.client')}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="phone"
                  render={({ field: { onChange, value }, formState: { errors } }) => (
                    <MuiTelInput
                      className="col-span-2"
                      defaultCountry="US"
                      forceCallingCode
                      error={!!errors.phone}
                      helperText={errors.phone?.message}
                      value={value}
                      onChange={onChange}
                      slotProps={{
                        inputLabel: {
                          shrink: true,
                        },
                      }}
                      variant="outlined"
                      label={t('label.phone')}
                      placeholder={t('placeholder.phone')}
                    />
                  )}
                />
              </div>
            </div>
          </LoaderWrapper>
        </DialogContent>
        <DialogActions>
          <div className="flex flex-wrap grow p-4 pt-0 justify-between">
            <BackButton onClick={onClose} autoFocus />
            <LoadingButton
              startIcon={<EditIcon />}
              color="primary"
              variant="contained"
              loading={isLoadingEditReservation}
              type="submit"
            >
              {t('button.update')}
            </LoadingButton>
          </div>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export default EditReservationModal;
